import { Amplify } from "aws-amplify";
import { Navigate, Outlet, Route, Routes, useLocation, useParams, BrowserRouter } from "react-router-dom";
import "@frontend/App.css";
import AppErrorBoundary from "@frontend/components/AppErrorBoundary/AppErrorBoundary";
import Header from "@frontend/components/Header/Header";
import { LearningWorkspacePage } from "@frontend/pages/learning-workspace/LearningWorkspacePage";
import { LearningSidebar } from "./components/LearningSidebar";
import { parseModuleOrder, resolveModuleAssignments } from "@frontend/pages/learning-workspace/moduleOrder";
import { awsConfig, config } from "./config";
import { learningWorkspace } from "./learning";
import { ChatSessionsContextProvider } from "@frontend/lib/contexts/ChatSessionsProvider";
import { CustomThemeContextProvider } from "@frontend/lib/contexts/ThemeProvider";
import { UserInfoContextProvider } from "@frontend/lib/contexts/UserInfoProvider";
import { useUserInfoContext } from "@frontend/lib/contexts/UserInfoContext";
import { Auth } from "@frontend/pages/auth/auth";
import { isPrinceAuthorized } from "@common/domain/auth";
import Unauthorized from "@frontend/pages/unauthorized/unauthorized";
import TextChatPage from "@frontend/pages/text-chat/TextChatPage";
import TranscriptsTab from "@frontend/pages/admin/TranscriptsTab";
import ErrorsTab from "@frontend/pages/admin/ErrorsTab";
import { AdminAuth } from "@frontend/components/Auth/AdminAuth";
import { SuperAdminAuth } from "@frontend/components/Auth/SuperAdminAuth";
import { Box, Typography, Container, Paper, Grid, Card, CardContent } from "@mui/material";

const publicAssetBaseUrl = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
const PRINCE_COMPANION_PANEL = {
  title: "Machiavelli's The Prince",
  imageAlt: 'Portrait of Niccolò Machiavelli',
  imageSrc: `${publicAssetBaseUrl}/machiavelli.jpg`,
} as const;

if (learningWorkspace.enabled && !config.learningPersonaId.trim()) {
  throw new Error('Learning Workspace requires a resolved persona ID when enabled');
}

Amplify.configure({
  Auth: {
    region: awsConfig.REGION,
    userPoolId: awsConfig.USER_POOL_ID,
    userPoolWebClientId: awsConfig.USER_POOL_APP_CLIENT_ID,
    mandatorySignIn: true,
    authenticationFlowType: "USER_PASSWORD_AUTH",
    oauth: {
      domain: awsConfig.AUTH_DOMAIN,
      scope: ["email", "openid"],
      redirectSignIn: awsConfig.WEB_URL,
      redirectSignOut: awsConfig.WEB_URL,
      responseType: "code",
    },
  },
});

// "About The Prince" - historical context and key ideas
const AboutThePrince = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: "1px solid var(--glass-border-subtle)", background: "var(--color-surface)" }}>
        <Typography variant="h3" sx={{ fontFamily: "'Newsreader', Georgia, serif", mb: 2, fontWeight: 500, letterSpacing: "-0.02em" }}>
          Niccolò Machiavelli and <em>The Prince</em>
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, fontStyle: "italic" }}>
          Written in 1513 amid the wars and political upheaval of Renaissance Italy
        </Typography>

        <Box sx={{ display: "grid", gap: 3, fontSize: "1.1rem", lineHeight: 1.6, color: "text.primary" }}>
          <Typography>
            Niccolò Machiavelli wrote <em>The Prince</em> after the Medici returned to power in Florence and removed him from public office. He dedicated the work to Lorenzo de' Medici, presenting it as a practical study of how rulers acquire, maintain, and lose states. It was published posthumously in 1532.
          </Typography>
          <Typography>
            The work asks readers to examine politics as it operates in practice rather than as they wish it would. Its central ideas include <em>virtù</em>—the skill, judgment, and force of character needed to shape events—and <em>fortuna</em>, the uncertainty that limits every leader's control. Machiavelli also considers military preparedness, reputation, fear, love, cruelty, and political necessity.
          </Typography>
          <Typography>
            Machiavelli does not simply praise deception or cruelty. He repeatedly asks when harsh actions succeed, when they create hatred, and how appearances affect political power. Use this companion to test those arguments, examine his historical examples, compare <em>The Prince</em> with the <em>Discourses on Livy</em>, and distinguish the text from modern caricatures of “Machiavellian” behavior.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

// "Activities to Try" - guided prompts for studying Machiavelli
const ActivitiesToTry = () => {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: "'Newsreader', Georgia, serif", mb: 2, fontWeight: 500, letterSpacing: "-0.02em" }}>
        Activities to Try
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Explore Machiavelli's arguments through close reading, comparison, and debate.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, background: "var(--color-surface)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                📖 Close-read a Chapter
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a passage from <em>The Prince</em>. Ask the companion to explain its argument, define important terms, and connect the passage to the chapter's larger purpose.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, background: "var(--color-surface)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                ⚖️ Debate Love and Fear
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Examine Machiavelli's famous comparison of being loved and feared. Identify the conditions and limits he places on his advice, then argue for or against his conclusion.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, background: "var(--color-surface)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                🎲 Compare Virtù and Fortune
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ask how skill, judgment, boldness, and chance interact in Machiavelli's account of political success. Apply the concepts to one of his historical examples.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: "100%", borderRadius: 3, background: "var(--color-surface)" }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                🏛️ Put the Advice on Trial
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Test a claim from <em>The Prince</em> against the <em>Discourses on Livy</em>, a historical leader, or a modern case. Ask what changes when the goal is a republic rather than a principality.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

const NavigateToTextChat = () => {
  const { urlChatSession } = useParams();
  return <Navigate to={`/text-chat/${urlChatSession}`} replace />;
};

const NavigateToLearningWorkspace = ({ modality }: { modality: 'textbook' | 'chat' | 'avatar' }) => {
  const { chapterSlug } = useParams<{ chapterSlug?: string }>();
  const chapterPath = chapterSlug ? `/${chapterSlug}` : '';
  return <Navigate to={`/learn/${learningWorkspace.persona.slug}/${modality}${chapterPath}`} replace />;
};

const NavigateToLearningHome = () => {
  const { userInfo } = useUserInfoContext();

  if (!learningWorkspace.enabled || learningWorkspace.materials.length === 0) {
    return <Navigate to="/text-chat" replace />;
  }

  // Module 1 is always first; which modality that is depends on this participant's
  // randomized assignment (custom:module_order), same as the sidebar.
  const [firstAssignment] = resolveModuleAssignments(
    learningWorkspace.materials,
    parseModuleOrder(userInfo?.moduleOrder),
  );
  if (!firstAssignment) {
    return <Navigate to="/text-chat" replace />;
  }
  return (
    <Navigate
      to={`/learn/${learningWorkspace.persona.slug}/${firstAssignment.modality}/${firstAssignment.chapterSlug}`}
      replace
    />
  );
};

const PrinceLayout = () => {
  const { pathname } = useLocation();
  const showSidebar = pathname.startsWith('/learn/') || pathname === '/admin' || pathname.startsWith('/admin/');
  const isWorkspaceRoute = pathname.startsWith('/learn/');

  return (
    <Box sx={{ display: "flex", minHeight: 0 }}>
      {showSidebar && <LearningSidebar workspace={learningWorkspace} />}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          height: "calc(100dvh - 48px)",
          overflowY: isWorkspaceRoute ? "auto" : "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

const LearningWorkspaceRoute = () => {
  const params = useParams<{
    personaSlug?: string;
    modality?: string;
    chapterSlug?: string;
  }>();

  if (
    params.personaSlug !== learningWorkspace.persona.slug ||
    !params.modality ||
    !['textbook', 'chat', 'avatar'].includes(params.modality)
  ) {
    return <NavigateToLearningHome />;
  }

  return (
    <LearningWorkspacePage
      nonTextbookMaterialPanel={PRINCE_COMPANION_PANEL}
      personaId={config.learningPersonaId}
      workspace={learningWorkspace}
    />
  );
};

export const Root = () => {
  return (
    <div>
      <Routes>
        <Route element={<CustomThemeContextProvider />}>
          <Route element={<UserInfoContextProvider />}>
            <Route element={<ChatSessionsContextProvider />}>
              <Route element={<Header />}>
                <Route element={<Auth showGoogleSignIn={false} isAuthorized={isPrinceAuthorized} />}>
                  <Route element={<PrinceLayout />}>
                    {/* Root "/" is the Learning Workspace home: the user's first module */}
                    <Route path="/" element={<NavigateToLearningHome />} />
                    <Route path="/chat" element={<Navigate to="/text-chat" replace />} />
                    <Route path="/chat/:urlChatSession" element={<NavigateToTextChat />} />
                    <Route path="/text-chat" element={<TextChatPage />} />
                    <Route path="/text-chat/:urlChatSession" element={<TextChatPage />} />
                    {/* Legacy links to the old top-level paths */}
                    <Route path="/about" element={<Navigate to="/learn/about" replace />} />
                    <Route path="/activities" element={<Navigate to="/learn/activities" replace />} />
                    <Route path="/learn/about" element={<AboutThePrince />} />
                    <Route path="/learn/activities" element={<ActivitiesToTry />} />
                    <Route path="/learn" element={<NavigateToLearningHome />} />
                    <Route path="/learn/:personaSlug" element={<NavigateToLearningHome />} />
                    <Route path="/learn/textbook/:chapterSlug?" element={<NavigateToLearningWorkspace modality="textbook" />} />
                    <Route path="/learn/chat/:chapterSlug?" element={<NavigateToLearningWorkspace modality="chat" />} />
                    <Route path="/learn/avatar/:chapterSlug?" element={<NavigateToLearningWorkspace modality="avatar" />} />
                    <Route
                      path="/learn/:personaSlug/:modality/:chapterSlug?"
                      element={<LearningWorkspaceRoute />}
                    />
                    <Route path="/learn/*" element={<NavigateToLearningHome />} />
                    <Route element={<AdminAuth />}>
                      <Route path="/admin" element={<Navigate to="/admin/transcripts" replace />} />
                      <Route path="/admin/transcripts" element={<TranscriptsTab defaultPersonaId={config.learningPersonaId} />} />
                    </Route>
                    <Route element={<SuperAdminAuth />}>
                      <Route path="/admin/errors" element={<ErrorsTab />} />
                    </Route>
                  </Route>
                </Route>
                <Route path="/unauthorized" element={<Unauthorized />} />
              </Route>
            </Route>
          </Route>
        </Route>
      </Routes>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AppErrorBoundary>
        <Root />
      </AppErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
